import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { Control, Controller, FieldErrors, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from '../../../core/components/Button.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';

const AddIcon = withIcon(faPlus);
const TrashIcon = withIcon(faTrash);

const HEADER_NAME_PATTERN = /^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/;

export interface AdditionalHeader {
    name: string;
    value: string;
}

const AdditionalHeadersField = ({
    control,
    errors,
}: {
    control: Control<any>;
    errors?: FieldErrors<{ additionalHeaders: AdditionalHeader[] }>['additionalHeaders'];
}) => {
    const { t } = useTranslation('integrations');
    const { fields, append, remove } = useFieldArray({ control, name: 'additionalHeaders' });

    return (
        <FormField
            label={t('dynamicsFinanceOperations.form.additionalHeaders')}
            helpText={t('dynamicsFinanceOperations.form.additionalHeadersHelpText')}
        >
            <div className="flex flex-col gap-2">
                {fields.map((field, index) => (
                    <div key={field.id} className="flex items-start gap-2">
                        <Controller
                            name={`additionalHeaders.${index}.name`}
                            control={control}
                            rules={{
                                required: t('dynamicsFinanceOperations.form.additionalHeaderNameRequired'),
                                pattern: {
                                    value: HEADER_NAME_PATTERN,
                                    message: t('dynamicsFinanceOperations.form.additionalHeaderNameInvalid'),
                                },
                                validate: (name: string) =>
                                    name.trim().toLowerCase() !== 'authorization' ||
                                    t('dynamicsFinanceOperations.form.additionalHeaderNameProtected'),
                            }}
                            render={({ field: nameField }) => (
                                <FormField className="flex-1" error={errors?.[index]?.name}>
                                    <StringField
                                        {...nameField}
                                        placeholder={t(
                                            'dynamicsFinanceOperations.form.additionalHeaderNamePlaceholder'
                                        )}
                                    />
                                </FormField>
                            )}
                        />
                        <Controller
                            name={`additionalHeaders.${index}.value`}
                            control={control}
                            rules={{
                                validate: (value: string) =>
                                    !!value.trim() || t('dynamicsFinanceOperations.form.additionalHeaderValueRequired'),
                            }}
                            render={({ field: valueField }) => (
                                <FormField className="flex-1" error={errors?.[index]?.value}>
                                    <StringField
                                        {...valueField}
                                        placeholder={t(
                                            'dynamicsFinanceOperations.form.additionalHeaderValuePlaceholder'
                                        )}
                                    />
                                </FormField>
                            )}
                        />
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => remove(index)}
                            title={t('dynamicsFinanceOperations.form.removeAdditionalHeader')}
                        >
                            <TrashIcon />
                        </Button>
                    </div>
                ))}
                <div>
                    <Button type="button" onClick={() => append({ name: '', value: '' })}>
                        <AddIcon /> {t('dynamicsFinanceOperations.form.addAdditionalHeader')}
                    </Button>
                </div>
            </div>
        </FormField>
    );
};

export default AdditionalHeadersField;
